/**
 * Addon configurations for features that can be unlocked via addons.
 * Maps feature keys to addon display config. Add addonKey for checkout handling.
 *
 * @typedef {Object} AddonConfig
 * @property {React.ComponentType} [icon] - Icon component (default: AddonIcon)
 * @property {string} label - Addon name (e.g. "Remove Dante AI Badge Add-On")
 * @property {string} subtitle - Short description
 * @property {string[]} features - Feature list for the addon
 * @property {string} price - Price display (e.g. "$25/month")
 * @property {string} buttonLabel - CTA button text
 * @property {string} [addonKey] - Key to find addon in plans for checkout (e.g. "remove_powered_by_dante_ai")
 */

import AddonIcon from '@/shared/icons/AddonIcon';
import AddonCreditsIcon from '@/shared/icons/AddonCreditsIcon';
import AddonExtraChatbotsIcon from '@/shared/icons/AddonExtraChatbotsIcon';
import AddonExtraTeamSeatsIcon from '@/shared/icons/AddonExtraTeamSeatsIcon';

export const ADDON_CONFIGS = {
  remove_powered_by_dante: {
    icon: AddonIcon,
    label: 'Remove Dante AI Badge Add-On',
    subtitle: 'Professional white-label appearance',
    features: ['Remove badge only (no custom CSS)'],
    price: '$25/month',
    buttonLabel: 'Remove with Add-On',
    addonKey: 'remove_powered_by_dante_ai',
  },
  credits: {
    icon: AddonCreditsIcon,
    label: 'Extra Message Credits Add-On',
    subtitle: '$10 per 1,000 message credits/month.',
    features: ['Add extra message credits in multiples of 1,000'],
    price: '$10/1,000 credits/month',
    buttonLabel: 'Add Credits',
    addonKey: 'extra_credits',
    /** Quantity selector: 1 = 1,000 credits, 2 = 2,000, etc. */
    hasInputQty: true,
    quantityOptions: [
      { value: 1, label: '1,000 credits', credits: 1000, pricePerMonth: 10 },
      { value: 2, label: '2,000 credits', credits: 2000, pricePerMonth: 20 },
      { value: 5, label: '5,000 credits', credits: 5000, pricePerMonth: 50 },
      { value: 10, label: '10,000 credits', credits: 10000, pricePerMonth: 100 },
      { value: 20, label: '20,000 credits', credits: 20000, pricePerMonth: 200 },
    ],
  },
  number_of_knowledgebases: {
    icon: AddonExtraChatbotsIcon,
    label: 'Extra Agents Add-On',
    subtitle: '$5 per 1 agent/month.',
    features: ['Add extra agents to your subscription'],
    price: '$5/agent/month',
    buttonLabel: 'Add Agent',
    addonKey: 'extra_chatbot',
  },
  chatbots_limit: {
    icon: AddonExtraChatbotsIcon,
    label: 'Extra Agents Add-On',
    subtitle: '$5 per 1 agent/month.',
    features: ['Add extra agents to your subscription'],
    price: '$5/agent/month',
    buttonLabel: 'Add Agent',
    addonKey: 'extra_chatbot',
  },
  team_management: {
    icon: AddonExtraTeamSeatsIcon,
    label: 'Extra Team Member Seats Add-On',
    subtitle: 'Add team members to collaborate on your agents.',
    features: ['Add extra team member seats to invite collaborators'],
    price: '$9/seat/month',
    buttonLabel: 'Add Team Member Seats',
    addonKey: 'extra_seat',
  },
};

/**
 * Subheader shown when modal offers both plan upgrade and add-on options.
 * Clarifies that users can either upgrade to the next tier or add the feature as an add-on.
 */
export const PLAN_AND_ADDON_SUBHEADER =
  'Upgrade to the next plan to unlock this feature, or add it as an add-on to your current plan. You choose what works best for you.';

/**
 * Get addon config for a feature, if it has an addon option.
 * @param {string} feature - Feature key
 * @returns {AddonConfig|null}
 */
export function getAddonConfigForFeature(feature) {
  return ADDON_CONFIGS[feature] ?? null;
}
